import { config } from '../config/config';

class StripeService {
  private readonly baseUrl = 'https://api.stripe.com/v1';
  private readonly publicKey: string;
  private readonly secretKey: string;

  constructor() {
    this.publicKey = config.stripePublicKey;
    this.secretKey = config.stripeSecretKey;
  }

  async initializePayment(params: {
    amount: number;
    reference: string;
    description: string;
  }) {
    try {
      // Vérifier si le montant est déjà en EUR
      let eurAmount;
      
      // Si le montant est déjà en EUR, le convertir en centimes
      if (params.amount < 100) { // Montant probablement déjà en EUR (moins de 100 EUR)
        eurAmount = Math.round(params.amount * 100); // Conversion en centimes
        console.log(`Montant déjà en EUR: ${params.amount} EUR = ${eurAmount} centimes`);
      } else {
        // Sinon, convertir de XAF vers EUR puis en centimes
        eurAmount = Math.round((params.amount / 500) * 100); // Montant en centimes d'euro
        console.log(`Conversion XAF vers EUR: ${params.amount} XAF = ${eurAmount} centimes`);
      }
      
      // S'assurer que le montant est d'au moins 50 centimes (minimum Stripe)
      const MIN_AMOUNT_CENTS = 50;
      if (eurAmount < MIN_AMOUNT_CENTS) {
        eurAmount = MIN_AMOUNT_CENTS;
        console.log(`Montant ajusté au minimum Stripe: ${MIN_AMOUNT_CENTS} centimes`);
      }

      // Créer d'abord un PaymentIntent
      const formData = new URLSearchParams({
        'amount': eurAmount.toString(),
        'currency': 'eur',
        'payment_method_types[]': 'card',
        'description': params.description,
        'metadata[original_amount_xaf]': params.amount.toString(),
        'metadata[reference]': params.reference
      });

      console.log('Requête Stripe (PaymentIntent):', formData.toString());

      const response = await fetch(`${this.baseUrl}/payment_intents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.secretKey}`,
          'Content-Type': 'application/x-www-form-urlencoded',
          'Stripe-Version': '2023-10-16'
        },
        body: formData
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('Erreur Stripe détaillée:', {
          status: response.status,
          statusText: response.statusText,
          error: responseData
        });
        throw new Error(responseData.error?.message || 'Échec de la création du PaymentIntent');
      }

      console.log('PaymentIntent créé:', responseData);

      // Retourner les informations nécessaires pour le frontend
      return {
        reference: responseData.id,
        payment_url: `${config.appUrl}/payment/stripe/process?payment_intent=${responseData.id}&client_secret=${responseData.client_secret}`,
        client_secret: responseData.client_secret,
        status: 'pending'
      };
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du paiement Stripe:', error);
      throw error;
    }
  }

  async verifyPayment(paymentIntentId: string) {
    try {
      const response = await fetch(`${this.baseUrl}/payment_intents/${paymentIntentId}?expand[]=payment_method`, {
        headers: {
          'Authorization': `Bearer ${this.secretKey}`,
          'Content-Type': 'application/x-www-form-urlencoded',
          'Stripe-Version': '2023-10-16'
        }
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('Erreur Stripe détaillée:', {
          status: response.status,
          statusText: response.statusText,
          error: responseData
        });
        throw new Error('Échec de la vérification du paiement Stripe');
      }

      console.log('PaymentIntent vérifié:', responseData);

      // Formater les données comme PayPal
      const formattedData = {
        id: responseData.id,
        status: responseData.status === 'succeeded' ? 'COMPLETED' : 'FAILED',
        payment_source: {
          card: {
            name: responseData.payment_method?.billing_details?.name || 'Non spécifié',
            email: responseData.payment_method?.billing_details?.email,
            phone: '23767111111111' // Numéro par défaut pour Stripe
          }
        },
        purchase_units: [{
          payments: {
            captures: [{
              id: responseData.latest_charge,
              amount: {
                value: (responseData.amount / 100).toFixed(2),
                currency_code: responseData.currency.toUpperCase()
              },
              status: responseData.status === 'succeeded' ? 'COMPLETED' : 'FAILED',
              create_time: new Date(responseData.created * 1000).toISOString(),
              update_time: new Date(responseData.created * 1000).toISOString()
            }]
          },
          reference_id: responseData.metadata?.reference
        }]
      };

      return {
        status: responseData.status === 'succeeded' ? 'completed' : 'failed',
        transactionId: responseData.id,
        amount: (responseData.amount / 100) * 500, // Conversion centimes EUR vers XAF
        payment_data: formattedData
      };
    } catch (error) {
      console.error('Erreur lors de la vérification du paiement Stripe:', error);
      throw error;
    }
  }
}

export const stripeService = new StripeService();
